import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type AdminCourseRow, type AdminUserDetail, type UserTypeRow } from '../../api';
import { useSession } from '../../session';

export function Person() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me, refresh } = useSession();
  const [data, setData] = useState<AdminUserDetail | null>(null);
  const [types, setTypes] = useState<UserTypeRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try { setData(await api.get<AdminUserDetail>(`/admin/users/${id}`)); }
    catch (e) { setError((e as ApiError).message); }
  }, [id]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { api.get<{ types: UserTypeRow[] }>('/admin/users/types').then((r) => setTypes(r.types)).catch(() => {}); }, []);

  if (error) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;
  const u = data.user;
  const isMe = me?.id === u.id;

  const patch = async (body: Record<string, unknown>, what: string) => {
    setBusy(true); setError(null); setNote(null);
    try {
      await api.patch(`/admin/users/${u.id}`, body);
      setNote(what);
      await load();
    } catch (e) {
      setError((e as ApiError).message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <nav className="crumbs"><Link to="/admin/people">People</Link> <span aria-hidden>›</span> {u.firstName} {u.lastName}</nav>

      <div className="page-head">
        <h1 className="h1">
          {u.firstName} {u.lastName}
          {u.status === 'inactive' && <span className="tag">deactivated</span>}
        </h1>
        <Link className="btn btn--ghost" to={`/progress/${u.id}`}>Where they are</Link>
        {u.status === 'active' && (
          <button className="btn btn--ghost" disabled={busy} onClick={async () => {
            // Answering "it does not work for me" by looking at what they see.
            setBusy(true);
            try {
              await api.post(`/admin/people/${u.id}/view-as`);
              await refresh();
              navigate('/');
            } catch (e) { setError((e as ApiError).message); }
            finally { setBusy(false); }
          }}>View the portal as them</button>
        )}
      </div>

      {note && <p className="notice">{note}</p>}
      {error && <p className="auth__error">{error}</p>}

      <dl className="facts facts--wide">
        <div><dt>Username</dt><dd className="mono">{u.login}</dd></div>
        <div><dt>Email</dt><dd>{u.email ?? '—'}</dd></div>
        <div><dt>Phone</dt><dd><PhoneField user={u} onSaved={setNote} onError={setError} /></dd></div>
        <div><dt>Type</dt><dd>{u.userTypeName}</dd></div>
        <div><dt>Password</dt><dd>{u.hasPassword ? 'set' : <>not yet — <Link to="/admin/passwords">send a link</Link></>}</dd></div>
        <div><dt>Last seen</dt><dd>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'never'}</dd></div>
      </dl>

      <section className="panel">
        <h2 className="h2">Account</h2>
        <div className="row">
          <label>
            <span>User type</span>
            <select
              value={u.userTypeId}
              disabled={busy || isMe}
              onChange={(e) => void patch({ userTypeId: Number(e.target.value) }, 'User type changed.')}
            >
              {types.map((t) => (
                <option key={t.id} value={t.id} disabled={!t.assignable && t.id !== u.userTypeId}>
                  {t.name}{!t.assignable && t.id !== u.userTypeId ? ' — beyond your own permissions' : ''}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Status</span>
            <select
              value={u.status}
              disabled={busy || isMe}
              onChange={(e) => void patch({ status: e.target.value }, e.target.value === 'inactive'
                ? 'Deactivated. Their training record is kept and they are signed out.'
                : 'Reactivated.')}
            >
              <option value="active">Active</option>
              <option value="inactive">Deactivated</option>
            </select>
          </label>
        </div>

        {isMe && <p className="muted small">This is your own account — change your own type or status from the database, not here.</p>}

        {u.hasPassword && !isMe && (
          <p>
            <button className="btn btn--ghost" disabled={busy} onClick={async () => {
              if (!confirm(`Clear ${u.firstName}'s password? They will choose a new one at their next sign-in, and any open session ends now.`)) return;
              setBusy(true); setError(null); setNote(null);
              try {
                const r = await api.post<{ note: string }>(`/admin/users/${u.id}/require-password-reset`);
                setNote(r.note); await load();
              } catch (e) { setError((e as ApiError).message); } finally { setBusy(false); }
            }}>Require a new password</button>
          </p>
        )}
      </section>

      <section className="panel">
        <div className="page-head">
          <h2 className="h2">Courses ({data.enrolments.length})</h2>
          <EnrolButton userId={u.id} onDone={load} taken={data.enrolments.map((e) => e.courseId)} />
        </div>
        {data.enrolments.length === 0 && <p className="muted">Not enrolled in anything yet.</p>}
        {data.enrolments.length > 0 && (
          <table className="grid">
            <thead><tr><th>Course</th><th>Role</th><th>Progress</th><th>Status</th><th /></tr></thead>
            <tbody>
              {data.enrolments.map((e) => (
                <tr key={e.courseId}>
                  <td>
                    <Link to={`/admin/courses/${e.courseId}`}>{e.courseName}</Link>
                    {e.courseStatus !== 'active' && <span className="tag">off</span>}
                  </td>
                  <td className="muted">{e.role}</td>
                  <td>
                    <div className="bar bar--sm"><span style={{ width: `${e.completionPct}%` }} /></div>
                    <span className="muted small">{e.completionPct}%{e.score != null ? ` · ${e.score}%` : ''}</span>
                  </td>
                  <td><span className={`pill pill--${e.status}`}>{e.status.replace('_', ' ')}</span></td>
                  <td>
                    <button className="link-danger" disabled={busy} onClick={async () => {
                      const hard = e.status === 'completed';
                      const msg = hard
                        ? `${u.firstName} has COMPLETED "${e.courseName}". Removing the enrolment erases that completion permanently. Continue?`
                        : `Remove ${u.firstName} from "${e.courseName}"? Their progress in it is lost.`;
                      if (!confirm(msg)) return;
                      setBusy(true); setError(null);
                      try {
                        await api.del(`/admin/users/${u.id}/enrolments/${e.courseId}${hard ? '?force=true' : ''}`);
                        setNote('Enrolment removed.'); await load();
                      } catch (err) { setError((err as ApiError).message); } finally { setBusy(false); }
                    }}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {data.groups.length > 0 && (
        <section className="panel">
          <h2 className="h2">Cohorts</h2>
          <ul className="chips">
            {data.groups.map((g) => <li key={g.id}><Link to={`/admin/cohorts/${g.id}`}>{g.name}</Link></li>)}
          </ul>
        </section>
      )}

      <p><button className="btn btn--ghost" onClick={() => navigate('/admin/people')}>Back to people</button></p>
    </>
  );
}

function EnrolButton({ userId, onDone, taken }: { userId: number; onDone: () => void; taken: number[] }) {
  const [open, setOpen] = useState(false);
  const [courses, setCourses] = useState<AdminCourseRow[] | null>(null);
  const [picked, setPicked] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || courses) return;
    api.get<{ courses: AdminCourseRow[] }>('/admin/courses?pageSize=200').then((r) => setCourses(r.courses)).catch(() => setCourses([]));
  }, [open, courses]);

  if (!open) return <button className="btn btn--primary" onClick={() => setOpen(true)}>Enrol on a course</button>;

  const available = (courses ?? []).filter((c) => !taken.includes(c.id));

  return (
    <div className="picker">
      <div className="picker__list">
        {!courses && <p className="muted">Loading…</p>}
        {courses && available.length === 0 && <p className="muted">Already on every course.</p>}
        {available.map((c) => (
          <label key={c.id}>
            <input type="checkbox" checked={picked.includes(c.id)}
              onChange={(e) => setPicked(e.target.checked ? [...picked, c.id] : picked.filter((x) => x !== c.id))} />
            <span>{c.name}{c.status !== 'active' && <span className="tag">off</span>}</span>
          </label>
        ))}
      </div>
      <div className="picker__actions">
        <button className="btn btn--primary" disabled={busy || !picked.length} onClick={async () => {
          setBusy(true);
          try {
            await api.post(`/admin/users/${userId}/enrolments`, { courseIds: picked, role: 'learner' });
            setPicked([]); setOpen(false); onDone();
          } finally { setBusy(false); }
        }}>Enrol on {picked.length || 'no'} course{picked.length === 1 ? '' : 's'}</button>
        <button className="btn btn--ghost" onClick={() => { setOpen(false); setPicked([]); }}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * The number WhatsApp messages go to. Edited here rather than on a form of its
 * own because it is the one field the team will be filling in for everybody:
 * the TalentLMS export carried no phone numbers at all.
 */
function PhoneField({ user, onSaved, onError }: {
  user: { id: number; phone?: string | null };
  onSaved: (m: string) => void;
  onError: (m: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user.phone ?? '');
  const [busy, setBusy] = useState(false);

  if (!editing) {
    return (
      <>
        {user.phone ?? <span className="muted">not known</span>}{' '}
        <button className="link-plain" onClick={() => { setValue(user.phone ?? ''); setEditing(true); }}>
          {user.phone ? 'change' : 'add'}
        </button>
      </>
    );
  }

  const save = async (next: string | null) => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}`, { phone: next });
      onSaved(next ? 'Phone number saved.' : 'Phone number removed.');
      setEditing(false);
      user.phone = next;
    } catch (e) { onError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  return (
    <span className="row">
      <input value={value} placeholder="+6281234567890" aria-label="Phone number"
        onChange={(e) => setValue(e.target.value)} />
      <button className="btn btn--primary btn--small" disabled={busy}
        onClick={() => void save(value.trim() || null)}>Save</button>
      <button className="link-plain" onClick={() => setEditing(false)}>cancel</button>
      <span className="muted small">International form, starting with +</span>
    </span>
  );
}
